import { useRef, useState } from 'react';
import styled from 'styled-components';
import SVG from 'react-inlinesvg';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from '@radix-ui/themes';
import * as Sentry from '@sentry/react';

import { PhoneFrame } from '../../../assets';
import Text from '../../../shared/Text';
import { htmlSanitizer } from '../../../lib/helpers';
import { downloadElementPng } from '../../../lib/helpers/downloadElementPng';
import { broadcastFileName } from '../../../lib/helpers/previewFileName';
import { useNotification } from '../../../shared/Notification/useNotification';
import { convertUrlsToLinks } from '../utils/textHelpers';
import defaultAttrs from '../../../lib/utils/defaultAttrs';

/**
 * A text message on a phone.
 *
 * @param {object} props
 * @param {string} props.message
 * @param {boolean} [props.downloadable] Offer Download Preview, which saves
 *   the phone as a PNG. Review sets it for a saved draft, as the email
 *   preview's button needs.
 * @param {string} [props.title] What the PNG's file name is made from: the
 *   Broadcast's subject, or a text-only Broadcast's text as written. Never
 *   `message`, which Review fills in for the person it previews as.
 */
export const SmsPreview = ({ message, downloadable = false, title }) => {
  const { t } = useTranslation('broadcasts');
  const boardName = useSelector(state => state.currentBoard.name);
  const { showError } = useNotification();
  const phoneRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadElementPng({
        element: phoneRef.current,
        fileName: broadcastFileName({
          board: boardName,
          title,
          channel: 'text',
        }),
      });
    } catch (error) {
      // A capture can reject with a DOM Event rather than an Error.
      Sentry.captureException(
        error instanceof Error
          ? error
          : new Error('Broadcast text preview download failed', {
              cause: error,
            }),
      );
      showError({ content: t('sms.downloadPngFailed') });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Container>
      {downloadable && (
        <Toolbar>
          <Button
            size="2"
            variant="outline"
            color="gray"
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? t('sms.downloadingPng') : t('sms.downloadPng')}
          </Button>
        </Toolbar>
      )}
      <Wrapper data-testid="sms-preview">
        <PhoneBox ref={phoneRef}>
          <Phone src={PhoneFrame} />
          <Content>
            <ChatMessages>
              {message && (
                <TextWrapper>
                  <BubbleText
                    dangerouslySetInnerHTML={htmlSanitizer(
                      convertUrlsToLinks(message),
                    )}
                  ></BubbleText>
                </TextWrapper>
              )}
            </ChatMessages>
          </Content>
        </PhoneBox>
      </Wrapper>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px 0;
`;

const Wrapper = styled.div`
  display: flex;
  overflow: hidden;
  justify-content: center;
  width: 100%;
  margin-top: 16px;
`;

// The phone and its message, and nothing else, so it is all the image holds.
const PhoneBox = styled.div`
  position: relative;
  display: flex;
`;

const Phone = styled(SVG)`
  height: 600px;
`;

const BubbleText = styled(Text).attrs(
  defaultAttrs({
    as: 'div',
    variant: 'small2',
  }),
)`
  padding: 12px 20px 10px 20px;
  margin: 10px 0;
  text-overflow: wrap;
  overflow-wrap: break-word;
`;

const TextWrapper = styled.div`
  margin: 0 16px;
  position: relative;
  color: white;
  border-radius: 25px 25px 25px 0;
  background: #e5e5ea;
  color: black;

  &:before {
    content: '';
    position: absolute;
    z-index: 2;
    bottom: -2px;
    left: -7px;
    height: 19px;
    border-left: 20px solid #e5e5ea;
    border-bottom-right-radius: 16px 14px;
    -webkit-transform: translate(0, -2px);
    transform: translate(0, -2px);
    border-bottom-right-radius: 15px 0px;
    transform: translate(-1px, -2px);
  }
  &:after {
    content: '';
    position: absolute;
    z-index: 3;
    bottom: -2px;
    left: 4px;
    width: 26px;
    height: 20px;
    background: white;
    border-bottom-right-radius: 10px;
    -webkit-transform: translate(-30px, -2px);
    transform: translate(-30px, -2px);
  }
`;

const ChatMessages = styled.div`
  display: block;
  position: relative;
  height: 85%;
  width: 100%;
  padding: 2% 3%;
`;

const Content = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  height: 90%;
  width: 260px;
  cursor: default;
  margin: auto;
`;
